import { useCallback } from 'react'
import { useLazyQuery } from '@apollo/client'

import { useNavigate } from 'router'
import { trackEvent } from 'analytics'

import { OPEREATION_FIND_DEVICE } from '../../operations-gql/queries'
import { TOPOLOGY_BY_SITE } from '../Topology/operations-gql/queries'
import { normalizeMac } from '../Topology/utils'
import { useResetFilters } from '../../store'

export const TOPOLOGY_TOOLTIP_KEYS = {
  loading: 'supervision.card.topology_loading',
  empty: 'supervision.card.no_topology_available',
  missing: 'supervision.card.no_equipment_topology',
  available: 'supervision.card.view_on_topology'
}

export const getEquipmentTopologyStatus = ({ loading, topology, mac }) => {
  if (loading) return 'loading'
  if (!topology?.nodes?.length) return 'empty'

  const normalizedMac = normalizeMac(mac)
  const containsEquipment = normalizedMac
    && topology.nodes.some(node => normalizeMac(node.mac_address || node.mac) === normalizedMac)

  return containsEquipment ? 'available' : 'missing'
}

export const useNavigateToEquipmentMap = (equipment) => {
  const navigate = useNavigate()
  const resetFilter = useResetFilters()
  const [findDevice, { loading }] = useLazyQuery(OPEREATION_FIND_DEVICE)

  const navigateToMap = useCallback(async () => {
    resetFilter()
    const { data } = await findDevice({
      variables: { uuid: equipment.uuid }
    })
    trackEvent('monitoring_equipment_located', { found: Boolean(data?.device?.position) })
    if (data?.device?.position) {
      navigate({
        pathname: `/monitoring/map/${equipment.site.sf_id}/${equipment.building_name}/${data.device.position.map.uuid}/${equipment.uuid}`
      })
    }
  }, [equipment, findDevice, navigate, resetFilter])

  return { navigateToMap, loading }
}

export const useNavigateToEquipmentTopology = (equipment, knownTopology) => {
  const navigate = useNavigate()
  const [findTopology, { loading }] = useLazyQuery(TOPOLOGY_BY_SITE, {
    fetchPolicy: 'network-only'
  })

  const navigateToTopology = useCallback(async () => {
    if (!equipment?.site?.sf_id || !equipment?.mac) return
    const topology = knownTopology === undefined
      ? (await findTopology({ variables: { sf_id: equipment.site.sf_id } })).data?.topology
      : knownTopology

    if (!topology?.nodes?.length) return
    trackEvent('monitoring_equipment_topology_opened')
    navigate({
      pathname: `/monitoring/topology/${equipment.site.sf_id}`,
      search: `?equipmentMac=${encodeURIComponent(equipment.mac)}`
    })
  }, [equipment, findTopology, knownTopology, navigate])

  return { navigateToTopology, loading }
}
